import React, { useEffect, useMemo, useRef, useState } from 'react'
import { Accordion, Button, Dropdown, Form, Modal } from 'react-bootstrap'
import { useTranslation } from 'react-i18next'
import { correspondentService } from '../../services/correspondent.service'
import { customFieldService } from '../../services/custom-field.service'
import { documentTypeService } from '../../services/document-type.service'
import { groupService } from '../../services/group.service'
import { PermissionAction, PermissionType } from '../../services/permissions.service'
import { storagePathService } from '../../services/storage-path.service'
import { userService } from '../../services/user.service'
import { workflowStatusService } from '../../services/workflow-status.service'
import { WorkflowAction, WorkflowActionType } from '../../types'
import { DocumentationDialog } from '../common/DocumentationDialog'
import { IfPermissions } from '../common/IfPermissions'
import { InputCustomFieldsValues } from '../common/InputCustomFieldsValues'
import { InputEntries } from '../common/InputEntries'
import { InputPermissionsGroup } from '../common/InputPermissionsGroup'
import { InputPermissionsUser } from '../common/InputPermissionsUser'
import { InputSelect } from '../common/InputSelect'
import { InputSwitch } from '../common/InputSwitch'
import { InputTags } from '../common/InputTags'
import { InputText } from '../common/InputText'
import { WorkflowPermissionActionFields } from './WorkflowPermissionActionFields'
import './ActionEditDialog.scss'

interface ActionEditDialogProps {
  action?: WorkflowAction | null
  onSuccess: (action: WorkflowAction) => void
  onError: (error: any) => void
  onClose: () => void
  onDelete?: () => void
}

const WORKFLOW_SELECT_MENU_Z_INDEX = 2300

export const ActionEditDialog: React.FC<ActionEditDialogProps> = ({ action, onSuccess, onError, onClose, onDelete }) => {
  const { t } = useTranslation()
  const [loading, setLoading] = useState(false)
  const [errors, setErrors] = useState<Record<string, string>>({})
  const [showDocsDialog, setShowDocsDialog] = useState(false)
  const [showPlaceholderDropdown, setShowPlaceholderDropdown] = useState(false)
  const [showEmailSubjectPlaceholderDropdown, setShowEmailSubjectPlaceholderDropdown] = useState(false)
  const [showEmailBodyPlaceholderDropdown, setShowEmailBodyPlaceholderDropdown] = useState(false)
  const [showWebhookBodyPlaceholderDropdown, setShowWebhookBodyPlaceholderDropdown] = useState(false)
  const placeholderButtonRef = useRef<HTMLButtonElement>(null)
  const emailSubjectPlaceholderButtonRef = useRef<HTMLButtonElement>(null)
  const emailBodyPlaceholderButtonRef = useRef<HTMLButtonElement>(null)
  const webhookBodyPlaceholderButtonRef = useRef<HTMLButtonElement>(null)
  const webhookBodyInputRef = useRef<HTMLTextAreaElement>(null)
  const titleInputRef = useRef<HTMLInputElement>(null)
  const emailSubjectInputRef = useRef<HTMLInputElement>(null)
  const emailBodyInputRef = useRef<HTMLTextAreaElement>(null)

  const [documentTypes, setDocumentTypes] = useState<any[]>([])
  const [correspondents, setCorrespondents] = useState<any[]>([])
  const [storagePaths, setStoragePaths] = useState<any[]>([])
  const [workflowStatuses, setWorkflowStatuses] = useState<any[]>([])
  const [users, setUsers] = useState<any[]>([])
  const [groups, setGroups] = useState<any[]>([])
  const [customFields, setCustomFields] = useState<any[]>([])

  const actionTypes = useMemo(
    () => [
      { value: WorkflowActionType.Assignment, label: t('assignment') },
      { value: WorkflowActionType.Removal, label: t('removal') },
      { value: WorkflowActionType.Email, label: t('email') },
      { value: WorkflowActionType.Webhook, label: t('webhook') },
      { value: WorkflowActionType.AttributeAndActionPermissions, label: t('workflow_action_attribute_action_permissions') },
    ],
    [t],
  )

  const [formData, setFormData] = useState<WorkflowAction>({
    type: WorkflowActionType.Assignment,
    assign_title: '',
    assign_tags: [],
    assign_document_type: undefined,
    assign_correspondent: undefined,
    assign_storage_path: undefined,
    assign_workflow_status: undefined,
    assign_owner: undefined,
    assign_custom_fields: [],
    assign_custom_fields_required: [],
    assign_custom_fields_values: {} as Record<number, any>,
    assign_view_users: [],
    assign_view_groups: [],
    assign_change_users: [],
    assign_change_groups: [],
    remove_tags: [],
    remove_all_tags: false,
    remove_document_types: [],
    remove_all_document_types: false,
    remove_correspondents: [],
    remove_all_correspondents: false,
    remove_storage_paths: [],
    remove_all_storage_paths: false,
    remove_workflow_status: false,
    remove_owners: [],
    remove_all_owners: false,
    remove_view_users: [],
    remove_view_groups: [],
    remove_change_users: [],
    remove_change_groups: [],
    remove_all_permissions: false,
    remove_custom_fields: [],
    remove_all_custom_fields: false,
    permission_document_type: null,
    permission_users: [],
    permission_groups: [],
    permission_attribute_rules: [],
    permission_action_rules: [],
    webhook: {
      url: '',
      use_params: false,
      as_json: false,
      params: {} as Record<string, string>,
      body: '',
      headers: {} as Record<string, string>,
      include_document: false,
    },
    email: {
      subject: '',
      body: '',
      to: '',
    },
  })

  const isEdit = !!action

  const loadData = async () => {
    try {
      const [documentTypesRes, correspondentsRes, storagePathsRes, workflowStatusesRes, usersRes, groupsRes, customFieldsRes] = await Promise.all([
        documentTypeService.listAll(),
        correspondentService.listAll(),
        storagePathService.listAll(),
        workflowStatusService.listAll(),
        userService.listAll({ full_perms: true }),
        groupService.listAll({ full_perms: true }),
        customFieldService.listAll(),
      ])

      setDocumentTypes(documentTypesRes.results || [])
      setCorrespondents(correspondentsRes.results || [])
      setStoragePaths(storagePathsRes.results || [])
      setWorkflowStatuses(workflowStatusesRes.results || [])
      setUsers(usersRes.results || [])
      setGroups(groupsRes.results || [])
      setCustomFields(customFieldsRes.results || [])
    } catch (error) {}
  }

  useEffect(() => {
    loadData()
    if (action) {
      setFormData({
        ...action,
        permission_document_type: action.permission_document_type ?? null,
        permission_users: action.permission_users || [],
        permission_groups: action.permission_groups || [],
        permission_attribute_rules: action.permission_attribute_rules || [],
        permission_action_rules: action.permission_action_rules || [],
      })
    } else {
      setFormData({
        type: WorkflowActionType.Assignment,
        assign_title: '',
        assign_tags: [],
        assign_document_type: undefined,
        assign_correspondent: undefined,
        assign_storage_path: undefined,
        assign_workflow_status: undefined,
        assign_owner: undefined,
        assign_custom_fields: [],
        assign_custom_fields_required: [],
        assign_custom_fields_values: {},
        assign_view_users: [],
        assign_view_groups: [],
        assign_change_users: [],
        assign_change_groups: [],
        remove_tags: [],
        remove_all_tags: false,
        remove_document_types: [],
        remove_all_document_types: false,
        remove_correspondents: [],
        remove_all_correspondents: false,
        remove_storage_paths: [],
        remove_all_storage_paths: false,
        remove_workflow_status: false,
        remove_owners: [],
        remove_all_owners: false,
        remove_view_users: [],
        remove_view_groups: [],
        remove_change_users: [],
        remove_change_groups: [],
        remove_all_permissions: false,
        remove_custom_fields: [],
        remove_all_custom_fields: false,
        permission_document_type: null,
        permission_users: [],
        permission_groups: [],
        permission_attribute_rules: [],
        permission_action_rules: [],
        webhook: {
          url: '',
          use_params: false,
          as_json: false,
          params: {} as Record<string, string>,
          body: '',
          headers: {} as Record<string, string>,
          include_document: false,
        },
        email: {
          subject: '',
          body: '',
          to: '',
          include_document: false,
        },
      })
    }
    setErrors({})
  }, [action])

  const handleInputChange = (field: keyof WorkflowAction, value: any) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }))
  }

  const placeholdersAllTriggers = [
    { placeholder: '{{correspondent}}', description: t('workflow_placeholder_correspondent') },
    { placeholder: '{{document_type}}', description: t('workflow_placeholder_document_type') },
    { placeholder: '{{owner_username}}', description: t('workflow_placeholder_owner_username') },
    { placeholder: '{{added}}', description: t('workflow_placeholder_added') },
    { placeholder: '{{added_year}}', description: t('workflow_placeholder_added_year') },
    { placeholder: '{{added_year_short}}', description: t('workflow_placeholder_added_year_short') },
    { placeholder: '{{added_month}}', description: t('workflow_placeholder_added_month') },
    { placeholder: '{{added_month_name}}', description: t('workflow_placeholder_added_month_name') },
    { placeholder: '{{added_month_name_short}}', description: t('workflow_placeholder_added_month_name_short') },
    { placeholder: '{{added_day}}', description: t('workflow_placeholder_added_day') },
    { placeholder: '{{added_time}}', description: t('workflow_placeholder_added_time') },
    { placeholder: '{{original_filename}}', description: t('workflow_placeholder_original_filename') },
    { placeholder: '{{filename}}', description: t('workflow_placeholder_filename') },
    { placeholder: '{{doc_title}}', description: t('workflow_placeholder_doc_title') },
  ]

  const placeholdersAddedUpdated = [
    { placeholder: '{{created}}', description: t('workflow_placeholder_created') },
    { placeholder: '{{created_year}}', description: t('workflow_placeholder_created_year') },
    { placeholder: '{{created_year_short}}', description: t('workflow_placeholder_created_year_short') },
    { placeholder: '{{created_month}}', description: t('workflow_placeholder_created_month') },
    { placeholder: '{{created_month_name}}', description: t('workflow_placeholder_created_month_name') },
    { placeholder: '{{created_month_name_short}}', description: t('workflow_placeholder_created_month_name_short') },
    { placeholder: '{{created_day}}', description: t('workflow_placeholder_created_day') },
    { placeholder: '{{created_time}}', description: t('workflow_placeholder_created_time') },
    { placeholder: '{{doc_url}}', description: t('workflow_placeholder_doc_url') },
  ]

  const placeholdersCustomFields = customFields
    .filter((cf) => cf?.data_type !== 'separator' && cf?.name)
    .map((cf) => {
      const safeName = String(cf.name).replace(/'/g, "\\'")
      const typeLabel = t(`custom_field_type_${cf.data_type}`, { defaultValue: cf.data_type })
      return {
        placeholder: `{{ custom_fields['${safeName}'].value }}`,
        description: `${cf.name} (${typeLabel})`,
      }
    })

  const handlePlaceholderSelect = (placeholder: string) => {
    const currentTitle = formData.assign_title || ''
    const newTitle = currentTitle + placeholder
    handleInputChange('assign_title', newTitle)
    setShowPlaceholderDropdown(false)

    setTimeout(() => {
      if (titleInputRef.current) {
        titleInputRef.current.focus()
        const input = titleInputRef.current
        const cursorPos = input.value.length
        input.setSelectionRange(cursorPos, cursorPos)
      }
    }, 0)
  }

  const handleEmailSubjectPlaceholderSelect = (placeholder: string) => {
    const currentSubject = formData.email?.subject || ''
    const newSubject = currentSubject + placeholder
    handleInputChange('email', { ...formData.email, subject: newSubject })
    setShowEmailSubjectPlaceholderDropdown(false)

    setTimeout(() => {
      if (emailSubjectInputRef.current) {
        emailSubjectInputRef.current.focus()
        const input = emailSubjectInputRef.current
        const cursorPos = input.value.length
        input.setSelectionRange(cursorPos, cursorPos)
      }
    }, 0)
  }

  const handleEmailBodyPlaceholderSelect = (placeholder: string) => {
    const currentBody = formData.email?.body || ''
    const newBody = currentBody + placeholder
    handleInputChange('email', { ...formData.email, body: newBody })
    setShowEmailBodyPlaceholderDropdown(false)

    setTimeout(() => {
      if (emailBodyInputRef.current) {
        emailBodyInputRef.current.focus()
        const textarea = emailBodyInputRef.current
        const cursorPos = textarea.value.length
        textarea.setSelectionRange(cursorPos, cursorPos)
      }
    }, 0)
  }

  const handleWebhookBodyPlaceholderSelect = (placeholder: string) => {
    const currentBody = formData.webhook?.body || ''
    handleInputChange('webhook', { ...formData.webhook, body: currentBody + placeholder })
    setShowWebhookBodyPlaceholderDropdown(false)

    setTimeout(() => {
      if (webhookBodyInputRef.current) {
        webhookBodyInputRef.current.focus()
        const textarea = webhookBodyInputRef.current
        const cursorPos = textarea.value.length
        textarea.setSelectionRange(cursorPos, cursorPos)
      }
    }, 0)
  }

  const handleSave = async () => {
    setLoading(true)
    setErrors({})

    try {
      onSuccess(formData)
    } catch (error: any) {
      onError(error)
    } finally {
      setLoading(false)
    }
  }

  const getTitle = () => {
    return isEdit ? t('edit_action') : t('add_action')
  }

  const handleDelete = () => {
    if (onDelete) {
      onClose()
      onDelete()
    }
  }

  return (
    <Modal show={true} onHide={onClose} size="xl" backdrop="static" className="action-edit-dialog">
      <Modal.Header closeButton>
        <Modal.Title>{getTitle()}</Modal.Title>
      </Modal.Header>

      <Modal.Body>
        <Form>
          {/* Action Type */}
          <Form.Group className="mb-3">
            <Form.Label>{t('action_type')}</Form.Label>
            <Form.Select value={formData.type} onChange={(e) => handleInputChange('type', parseInt(e.target.value))} isInvalid={!!errors.type}>
              {actionTypes.map((type) => (
                <option key={type.value} value={type.value}>
                  {type.label}
                </option>
              ))}
            </Form.Select>
            <Form.Control.Feedback type="invalid">{errors.type}</Form.Control.Feedback>
          </Form.Group>

          {formData.type === WorkflowActionType.AttributeAndActionPermissions && (
            <WorkflowPermissionActionFields
              formData={formData}
              documentTypes={documentTypes}
              customFields={customFields}
              users={users}
              groups={groups}
              onChange={handleInputChange}
            />
          )}

          {/* Assignment Fields */}
          {formData.type === WorkflowActionType.Assignment && (
            <div className="row">
              <div className="col">
                {/* Assign Title */}
                <div className="mb-3">
                  <label className="form-label d-flex align-items-center gap-2">
                    <span>{t('assign_title')}</span>
                    <Dropdown show={showPlaceholderDropdown} onToggle={setShowPlaceholderDropdown}>
                      <Dropdown.Toggle
                        ref={placeholderButtonRef}
                        variant="link"
                        size="sm"
                        className="p-0 text-muted"
                        style={{ textDecoration: 'none', lineHeight: '1' }}
                        title={t('available_placeholders') || 'Dostupni placeholder-i'}
                      >
                        <i className="bi bi-code-square" style={{ fontSize: '1.1em' }}></i>
                      </Dropdown.Toggle>
                      <Dropdown.Menu className="shadow" style={{ maxHeight: '400px', overflowY: 'auto', minWidth: '300px' }}>
                        <Dropdown.Header>
                          <small className="fw-bold">
                            {t('placeholders_available_for_all_trigger_types') || 'Placeholder-i Dostupni za Sve Tipove Okidača'}
                          </small>
                        </Dropdown.Header>
                        {placeholdersAllTriggers.map((item, index) => (
                          <Dropdown.Item
                            key={`all-${index}`}
                            onClick={() => handlePlaceholderSelect(item.placeholder)}
                            className="d-flex flex-column align-items-start"
                            style={{ padding: '0.5rem 1rem' }}
                          >
                            <code className="text-primary mb-1">{item.placeholder}</code>
                            <small className="text-muted">{item.description}</small>
                          </Dropdown.Item>
                        ))}
                        <Dropdown.Divider />
                        <Dropdown.Header>
                          <small className="fw-bold">
                            {t('placeholders_available_only_for_added_or_updated_triggers') ||
                              "Placeholder-i Dostupni Samo za Okidače 'Dodat' ili 'Ažuriran'"}
                          </small>
                        </Dropdown.Header>
                        {placeholdersAddedUpdated.map((item, index) => (
                          <Dropdown.Item
                            key={`added-${index}`}
                            onClick={() => handlePlaceholderSelect(item.placeholder)}
                            className="d-flex flex-column align-items-start"
                            style={{ padding: '0.5rem 1rem' }}
                          >
                            <code className="text-primary mb-1">{item.placeholder}</code>
                            <small className="text-muted">{item.description}</small>
                          </Dropdown.Item>
                        ))}
                        {placeholdersCustomFields.length > 0 && (
                          <>
                            <Dropdown.Divider />
                            <Dropdown.Header>
                              <small className="fw-bold">{t('placeholders_custom_fields_section_title')}</small>
                            </Dropdown.Header>
                            {placeholdersCustomFields.map((item, index) => (
                              <Dropdown.Item
                                key={`cf-${index}`}
                                onClick={() => handlePlaceholderSelect(item.placeholder)}
                                className="d-flex flex-column align-items-start"
                                style={{ padding: '0.5rem 1rem' }}
                              >
                                <code className="text-primary mb-1">{item.placeholder}</code>
                                <small className="text-muted">{item.description}</small>
                              </Dropdown.Item>
                            ))}
                          </>
                        )}
                      </Dropdown.Menu>
                    </Dropdown>
                  </label>
                  <Form.Control
                    ref={titleInputRef}
                    type="text"
                    value={formData.assign_title || ''}
                    onChange={(e) => handleInputChange('assign_title', e.target.value)}
                    placeholder={t('assign_title_placeholder_example')}
                    isInvalid={!!errors.assign_title}
                  />
                  {errors.assign_title && <Form.Control.Feedback type="invalid">{errors.assign_title}</Form.Control.Feedback>}
                  <small className="form-text text-muted">
                    {t('assign_title_description').replace(/<a[^>]*>.*?<\/a>/gi, '')}{' '}
                    <a
                      href="#"
                      onClick={(e) => {
                        e.preventDefault()
                        setShowDocsDialog(true)
                      }}
                      style={{ cursor: 'pointer' }}
                    >
                      {t('documentation')}
                    </a>
                    .
                    <br />
                    <strong>{t('example')}:</strong> {t('assign_title_example')}
                  </small>
                </div>

                {/* Assign Tags */}
                <InputTags
                  title={t('assign_tags')}
                  value={formData.assign_tags || []}
                  onChange={(value) => handleInputChange('assign_tags', value)}
                  disabled={false}
                  multiple={true}
                />

                {/* Assign Document Type */}
                <InputSelect
                  title={t('assign_document_type')}
                  value={formData.assign_document_type}
                  onChange={(value) => handleInputChange('assign_document_type', value)}
                  items={documentTypes}
                  bindLabel="name"
                  placeholder={t('select_option')}
                  error={errors.assign_document_type}
                />

                {/* Assign Correspondent */}
                <InputSelect
                  title={t('assign_correspondent')}
                  value={formData.assign_correspondent}
                  onChange={(value) => handleInputChange('assign_correspondent', value)}
                  items={correspondents}
                  bindLabel="name"
                  placeholder={t('select_option')}
                  error={errors.assign_correspondent}
                />

                <InputSelect
                  title={t('assign_storage_path')}
                  value={formData.assign_storage_path}
                  onChange={(value) => handleInputChange('assign_storage_path', value)}
                  items={storagePaths}
                  bindLabel="name"
                  placeholder={t('select_option')}
                  error={errors.assign_storage_path}
                />

                {/* Assign workflow status (document status) */}
                <InputSelect
                  title={t('document_workflow.workflow_status')}
                  value={formData.assign_workflow_status ?? undefined}
                  onChange={(value) => handleInputChange('assign_workflow_status', value ?? undefined)}
                  items={workflowStatuses}
                  bindLabel="name"
                  placeholder={t('select_option')}
                  error={errors.assign_workflow_status}
                />

                {/* Assign Custom Fields */}
                <InputSelect
                  title={t('assign_custom_fields')}
                  value={formData.assign_custom_fields || []}
                  onChange={(value) => {
                    const nextFields = value || []
                    setFormData((prev) => ({
                      ...prev,
                      assign_custom_fields: nextFields,
                      assign_custom_fields_required: (prev.assign_custom_fields_required || []).filter((id) =>
                        nextFields.includes(id),
                      ),
                    }))
                  }}
                  items={customFields}
                  bindLabel="name"
                  placeholder={t('select_option')}
                  error={errors.assign_custom_fields}
                  multiple={true}
                />

                {/* Custom Fields Values */}
                <InputCustomFieldsValues
                  value={formData.assign_custom_fields_values || {}}
                  onChange={(value) => handleInputChange('assign_custom_fields_values', value)}
                  selectedFields={formData.assign_custom_fields || []}
                  requiredFields={formData.assign_custom_fields_required || []}
                  onRequiredFieldsChange={(value) => handleInputChange('assign_custom_fields_required', value)}
                  onRemoveSelectedField={(fieldId) => {
                    setFormData((prev) => {
                      const newValues = { ...(prev.assign_custom_fields_values || {}) }
                      delete newValues[fieldId]
                      return {
                        ...prev,
                        assign_custom_fields: (prev.assign_custom_fields || []).filter((id) => id !== fieldId),
                        assign_custom_fields_values: newValues,
                        assign_custom_fields_required: (prev.assign_custom_fields_required || []).filter(
                          (id) => id !== fieldId,
                        ),
                      }
                    })
                  }}
                />
              </div>
              <div className="col">
                {/* Assign Owner */}
                <InputSelect
                  title={t('assign_owner')}
                  value={formData.assign_owner}
                  onChange={(value) => handleInputChange('assign_owner', value)}
                  items={users}
                  bindLabel="username"
                  placeholder={t('select_option')}
                  error={errors.assign_owner}
                />

                {/* Assign View Permissions */}
                <div>
                  <label className="form-label">{t('assign_view_permissions')}</label>
                  <div className="mb-2">
                    <div className="row mb-1">
                      <div className="col-lg-3">
                        <label className="form-label d-block my-2 text-nowrap">{t('users')}:</label>
                      </div>
                      <div className="col-lg-9">
                        <InputPermissionsUser
                          value={formData.assign_view_users || []}
                          users={users}
                          menuPortalZIndex={WORKFLOW_SELECT_MENU_Z_INDEX}
                          onChange={(value) => handleInputChange('assign_view_users', value)}
                          disabled={false}
                        />
                      </div>
                    </div>
                    <div className="row">
                      <div className="col-lg-3">
                        <label className="form-label d-block my-2 text-nowrap">{t('groups')}:</label>
                      </div>
                      <div className="col-lg-9">
                        <InputPermissionsGroup
                          value={formData.assign_view_groups || []}
                          groups={groups}
                          menuPortalZIndex={WORKFLOW_SELECT_MENU_Z_INDEX}
                          onChange={(value) => handleInputChange('assign_view_groups', value)}
                          disabled={false}
                        />
                      </div>
                    </div>
                  </div>
                  <label className="form-label">{t('assign_change_permissions')}</label>
                  <div>
                    <div className="row mb-1">
                      <div className="col-lg-3">
                        <label className="form-label d-block my-2 text-nowrap">{t('users')}:</label>
                      </div>
                      <div className="col-lg-9">
                        <InputPermissionsUser
                          value={formData.assign_change_users || []}
                          users={users}
                          menuPortalZIndex={WORKFLOW_SELECT_MENU_Z_INDEX}
                          onChange={(value) => handleInputChange('assign_change_users', value)}
                          disabled={false}
                        />
                      </div>
                    </div>
                    <div className="row">
                      <div className="col-lg-3">
                        <label className="form-label d-block my-2 text-nowrap">{t('groups')}:</label>
                      </div>
                      <div className="col-lg-9">
                        <InputPermissionsGroup
                          value={formData.assign_change_groups || []}
                          groups={groups}
                          menuPortalZIndex={WORKFLOW_SELECT_MENU_Z_INDEX}
                          onChange={(value) => handleInputChange('assign_change_groups', value)}
                          disabled={false}
                        />
                      </div>
                    </div>
                    <small className="form-text text-muted text-end d-block">{t('edit_permissions_also_grant_view')}</small>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Removal Fields */}
          {formData.type === WorkflowActionType.Removal && (
            <Accordion>
              <Accordion.Item eventKey="0">
                <Accordion.Header>
                  <i className="bi bi-trash me-2"></i>
                  {t('removal_fields')}
                </Accordion.Header>
                <Accordion.Body>
                  <div className="workflow-removal-form">
                    <div className="row">
                      <div className="col-md-6">
                        {/* Remove Tags */}
                        <div className="mb-3">
                          <h6 className="form-label">{t('remove_tags')}</h6>
                          <InputSwitch
                            title={t('remove_all')}
                            value={formData.remove_all_tags || false}
                            onChange={(value) => handleInputChange('remove_all_tags', value)}
                            horizontal={true}
                          />
                          <div className="mt-n3">
                            <InputTags
                              value={formData.remove_tags || []}
                              onChange={(value) => handleInputChange('remove_tags', value)}
                              disabled={formData.remove_all_tags}
                              multiple={true}
                            />
                          </div>
                        </div>

                        {/* Remove Correspondents */}
                        <div className="mb-3">
                          <h6 className="form-label">{t('remove_correspondents')}</h6>
                          <InputSwitch
                            title={t('remove_all')}
                            value={formData.remove_all_correspondents || false}
                            onChange={(value) => handleInputChange('remove_all_correspondents', value)}
                            horizontal={true}
                          />
                          <div className="mt-n3">
                            <InputSelect
                              value={formData.remove_correspondents || []}
                              onChange={(value) => handleInputChange('remove_correspondents', value)}
                              items={correspondents}
                              multiple={true}
                              disabled={formData.remove_all_correspondents}
                            />
                          </div>
                        </div>

                        {/* Remove Document Types */}
                        <div className="mb-3">
                          <h6 className="form-label">{t('remove_document_types')}</h6>
                          <InputSwitch
                            title={t('remove_all')}
                            value={formData.remove_all_document_types || false}
                            onChange={(value) => handleInputChange('remove_all_document_types', value)}
                            horizontal={true}
                          />
                          <div className="mt-n3">
                            <InputSelect
                              value={formData.remove_document_types || []}
                              onChange={(value) => handleInputChange('remove_document_types', value)}
                              items={documentTypes}
                              multiple={true}
                              disabled={formData.remove_all_document_types}
                            />
                          </div>
                        </div>

                        {/* Remove Storage Paths */}
                        <div className="mb-3">
                          <h6 className="form-label">{t('remove_storage_paths')}</h6>
                          <InputSwitch
                            title={t('remove_all')}
                            value={formData.remove_all_storage_paths || false}
                            onChange={(value) => handleInputChange('remove_all_storage_paths', value)}
                            horizontal={true}
                          />
                          <div className="mt-n3">
                            <InputSelect
                              value={formData.remove_storage_paths || []}
                              onChange={(value) => handleInputChange('remove_storage_paths', value)}
                              items={storagePaths}
                              multiple={true}
                              disabled={formData.remove_all_storage_paths}
                            />
                          </div>
                        </div>

                        {/* Remove workflow status (clear document status) */}
                        <div className="mb-3">
                          <h6 className="form-label">{t('document_workflow.workflow_status')}</h6>
                          <InputSwitch
                            title={t('remove_workflow_status')}
                            value={formData.remove_workflow_status || false}
                            onChange={(value) => handleInputChange('remove_workflow_status', value)}
                            horizontal={true}
                          />
                        </div>

                        {/* Remove Custom Fields */}
                        <div className="mb-3">
                          <h6 className="form-label">{t('remove_custom_fields')}</h6>
                          <InputSwitch
                            title={t('remove_all')}
                            value={formData.remove_all_custom_fields || false}
                            onChange={(value) => handleInputChange('remove_all_custom_fields', value)}
                            horizontal={true}
                          />
                          <div className="mt-n3">
                            <InputSelect
                              value={formData.remove_custom_fields || []}
                              onChange={(value) => handleInputChange('remove_custom_fields', value)}
                              items={customFields}
                              multiple={true}
                              disabled={formData.remove_all_custom_fields}
                            />
                          </div>
                        </div>
                      </div>

                      <div className="col-md-6">
                        {/* Remove Owners */}
                        <div className="mb-3">
                          <h6 className="form-label">{t('remove_owners')}</h6>
                          <InputSwitch
                            title={t('remove_all')}
                            value={formData.remove_all_owners || false}
                            onChange={(value) => handleInputChange('remove_all_owners', value)}
                            horizontal={true}
                          />
                          <div className="mt-n3">
                            <InputSelect
                              value={formData.remove_owners || []}
                              onChange={(value) => handleInputChange('remove_owners', value)}
                              items={users}
                              bindLabel="username"
                              multiple={true}
                              disabled={formData.remove_all_owners}
                            />
                          </div>
                        </div>

                        {/* Remove Permissions */}
                        <div className="mb-3">
                          <h6 className="form-label">{t('remove_permissions')}</h6>
                          <InputSwitch
                            title={t('remove_all')}
                            value={formData.remove_all_permissions || false}
                            onChange={(value) => handleInputChange('remove_all_permissions', value)}
                            horizontal={true}
                          />
                          <div>
                            <label className="form-label">{t('view_permissions')}</label>
                            <div className="mb-2">
                              <div className="row mb-1">
                                <div className="col-lg-3">
                                  <label className="form-label d-block my-2 text-nowrap">{t('users')}:</label>
                                </div>
                                <div className="col-lg-9">
                                  <InputPermissionsUser
                                    value={formData.remove_view_users || []}
                                    users={users}
                                    menuPortalZIndex={WORKFLOW_SELECT_MENU_Z_INDEX}
                                    onChange={(value) => handleInputChange('remove_view_users', value)}
                                    disabled={formData.remove_all_permissions}
                                  />
                                </div>
                              </div>
                              <div className="row">
                                <div className="col-lg-3">
                                  <label className="form-label d-block my-2 text-nowrap">{t('groups')}:</label>
                                </div>
                                <div className="col-lg-9">
                                  <InputPermissionsGroup
                                    value={formData.remove_view_groups || []}
                                    groups={groups}
                                    menuPortalZIndex={WORKFLOW_SELECT_MENU_Z_INDEX}
                                    onChange={(value) => handleInputChange('remove_view_groups', value)}
                                    disabled={formData.remove_all_permissions}
                                  />
                                </div>
                              </div>
                            </div>
                            <label className="form-label">{t('edit_permissions')}</label>
                            <div>
                              <div className="row mb-1">
                                <div className="col-lg-3">
                                  <label className="form-label d-block my-2 text-nowrap">{t('users')}:</label>
                                </div>
                                <div className="col-lg-9">
                                  <InputPermissionsUser
                                    value={formData.remove_change_users || []}
                                    users={users}
                                    menuPortalZIndex={WORKFLOW_SELECT_MENU_Z_INDEX}
                                    onChange={(value) => handleInputChange('remove_change_users', value)}
                                    disabled={formData.remove_all_permissions}
                                  />
                                </div>
                              </div>
                              <div className="row">
                                <div className="col-lg-3">
                                  <label className="form-label d-block my-2 text-nowrap">{t('groups')}:</label>
                                </div>
                                <div className="col-lg-9">
                                  <InputPermissionsGroup
                                    value={formData.remove_change_groups || []}
                                    groups={groups}
                                    menuPortalZIndex={WORKFLOW_SELECT_MENU_Z_INDEX}
                                    onChange={(value) => handleInputChange('remove_change_groups', value)}
                                    disabled={formData.remove_all_permissions}
                                  />
                                </div>
                              </div>
                              <small className="form-text text-muted text-end d-block">{t('edit_permissions_also_grant_view')}</small>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </Accordion.Body>
              </Accordion.Item>
            </Accordion>
          )}

          {/* Email Fields */}
          {formData.type === WorkflowActionType.Email && (
            <div className="row">
              <div className="col">
                <InputText
                  title={t('email_to')}
                  value={formData.email?.to || ''}
                  onChange={(value) => handleInputChange('email', { ...formData.email, to: value })}
                  error={errors.email_to}
                  placeholder="user@example.com, admin@example.com"
                  hint={t('email_to_hint')}
                />

                <div className="mb-3">
                  <label className="form-label d-flex align-items-center gap-2">
                    <span>{t('email_subject')}</span>
                    <Dropdown show={showEmailSubjectPlaceholderDropdown} onToggle={setShowEmailSubjectPlaceholderDropdown}>
                      <Dropdown.Toggle
                        ref={emailSubjectPlaceholderButtonRef}
                        variant="link"
                        size="sm"
                        className="p-0 text-muted"
                        style={{ textDecoration: 'none', lineHeight: '1' }}
                        title={t('available_placeholders') || 'Dostupni placeholder-i'}
                      >
                        <i className="bi bi-code-square" style={{ fontSize: '1.1em' }}></i>
                      </Dropdown.Toggle>
                      <Dropdown.Menu className="shadow" style={{ maxHeight: '400px', overflowY: 'auto', minWidth: '300px' }}>
                        <Dropdown.Header>
                          <small className="fw-bold">
                            {t('placeholders_available_for_all_trigger_types') || 'Placeholder-i Dostupni za Sve Tipove Okidača'}
                          </small>
                        </Dropdown.Header>
                        {placeholdersAllTriggers.map((item, index) => (
                          <Dropdown.Item
                            key={`all-${index}`}
                            onClick={() => handleEmailSubjectPlaceholderSelect(item.placeholder)}
                            className="d-flex flex-column align-items-start"
                            style={{ padding: '0.5rem 1rem' }}
                          >
                            <code className="text-primary mb-1">{item.placeholder}</code>
                            <small className="text-muted">{item.description}</small>
                          </Dropdown.Item>
                        ))}
                        <Dropdown.Divider />
                        <Dropdown.Header>
                          <small className="fw-bold">
                            {t('placeholders_available_only_for_added_or_updated_triggers') ||
                              "Placeholder-i Dostupni Samo za Okidače 'Dodat' ili 'Ažuriran'"}
                          </small>
                        </Dropdown.Header>
                        {placeholdersAddedUpdated.map((item, index) => (
                          <Dropdown.Item
                            key={`added-${index}`}
                            onClick={() => handleEmailSubjectPlaceholderSelect(item.placeholder)}
                            className="d-flex flex-column align-items-start"
                            style={{ padding: '0.5rem 1rem' }}
                          >
                            <code className="text-primary mb-1">{item.placeholder}</code>
                            <small className="text-muted">{item.description}</small>
                          </Dropdown.Item>
                        ))}
                        {placeholdersCustomFields.length > 0 && (
                          <>
                            <Dropdown.Divider />
                            <Dropdown.Header>
                              <small className="fw-bold">{t('placeholders_custom_fields_section_title')}</small>
                            </Dropdown.Header>
                            {placeholdersCustomFields.map((item, index) => (
                              <Dropdown.Item
                                key={`cf-${index}`}
                                onClick={() => handleEmailSubjectPlaceholderSelect(item.placeholder)}
                                className="d-flex flex-column align-items-start"
                                style={{ padding: '0.5rem 1rem' }}
                              >
                                <code className="text-primary mb-1">{item.placeholder}</code>
                                <small className="text-muted">{item.description}</small>
                              </Dropdown.Item>
                            ))}
                          </>
                        )}
                      </Dropdown.Menu>
                    </Dropdown>
                  </label>
                  <Form.Control
                    ref={emailSubjectInputRef}
                    type="text"
                    value={formData.email?.subject || ''}
                    onChange={(e) => handleInputChange('email', { ...formData.email, subject: e.target.value })}
                    placeholder="New {{document_type}} received"
                    isInvalid={!!errors.email_subject}
                  />
                  {errors.email_subject && <Form.Control.Feedback type="invalid">{errors.email_subject}</Form.Control.Feedback>}
                  {t('email_subject_hint') && <small className="form-text text-muted">{t('email_subject_hint')}</small>}
                </div>

                <div className="mb-3">
                  <label className="form-label d-flex align-items-center gap-2">
                    <span>{t('email_body')}</span>
                    <Dropdown show={showEmailBodyPlaceholderDropdown} onToggle={setShowEmailBodyPlaceholderDropdown}>
                      <Dropdown.Toggle
                        ref={emailBodyPlaceholderButtonRef}
                        variant="link"
                        size="sm"
                        className="p-0 text-muted"
                        style={{ textDecoration: 'none', lineHeight: '1' }}
                        title={t('available_placeholders') || 'Dostupni placeholder-i'}
                      >
                        <i className="bi bi-code-square" style={{ fontSize: '1.1em' }}></i>
                      </Dropdown.Toggle>
                      <Dropdown.Menu className="shadow" style={{ maxHeight: '400px', overflowY: 'auto', minWidth: '300px' }}>
                        <Dropdown.Header>
                          <small className="fw-bold">
                            {t('placeholders_available_for_all_trigger_types') || 'Placeholder-i Dostupni za Sve Tipove Okidača'}
                          </small>
                        </Dropdown.Header>
                        {placeholdersAllTriggers.map((item, index) => (
                          <Dropdown.Item
                            key={`all-${index}`}
                            onClick={() => handleEmailBodyPlaceholderSelect(item.placeholder)}
                            className="d-flex flex-column align-items-start"
                            style={{ padding: '0.5rem 1rem' }}
                          >
                            <code className="text-primary mb-1">{item.placeholder}</code>
                            <small className="text-muted">{item.description}</small>
                          </Dropdown.Item>
                        ))}
                        <Dropdown.Divider />
                        <Dropdown.Header>
                          <small className="fw-bold">
                            {t('placeholders_available_only_for_added_or_updated_triggers') ||
                              "Placeholder-i Dostupni Samo za Okidače 'Dodat' ili 'Ažuriran'"}
                          </small>
                        </Dropdown.Header>
                        {placeholdersAddedUpdated.map((item, index) => (
                          <Dropdown.Item
                            key={`added-${index}`}
                            onClick={() => handleEmailBodyPlaceholderSelect(item.placeholder)}
                            className="d-flex flex-column align-items-start"
                            style={{ padding: '0.5rem 1rem' }}
                          >
                            <code className="text-primary mb-1">{item.placeholder}</code>
                            <small className="text-muted">{item.description}</small>
                          </Dropdown.Item>
                        ))}
                        {placeholdersCustomFields.length > 0 && (
                          <>
                            <Dropdown.Divider />
                            <Dropdown.Header>
                              <small className="fw-bold">{t('placeholders_custom_fields_section_title')}</small>
                            </Dropdown.Header>
                            {placeholdersCustomFields.map((item, index) => (
                              <Dropdown.Item
                                key={`cf-${index}`}
                                onClick={() => handleEmailBodyPlaceholderSelect(item.placeholder)}
                                className="d-flex flex-column align-items-start"
                                style={{ padding: '0.5rem 1rem' }}
                              >
                                <code className="text-primary mb-1">{item.placeholder}</code>
                                <small className="text-muted">{item.description}</small>
                              </Dropdown.Item>
                            ))}
                          </>
                        )}
                      </Dropdown.Menu>
                    </Dropdown>
                  </label>
                  <Form.Control
                    ref={emailBodyInputRef}
                    as="textarea"
                    rows={6}
                    value={formData.email?.body || ''}
                    onChange={(e) => handleInputChange('email', { ...formData.email, body: e.target.value })}
                    placeholder="A new {{document_type}} has been added to the system."
                    isInvalid={!!errors.email_body}
                  />
                  {errors.email_body && <Form.Control.Feedback type="invalid">{errors.email_body}</Form.Control.Feedback>}
                  {t('email_body_hint') && <small className="form-text text-muted">{t('email_body_hint')}</small>}
                </div>

                <InputSwitch
                  id="email-include-document"
                  title={t('include_document')}
                  value={formData.email?.include_document || false}
                  onChange={(value) => handleInputChange('email', { ...formData.email, include_document: value })}
                  horizontal={true}
                />
              </div>
            </div>
          )}

          {/* Webhook Fields */}
          {formData.type === WorkflowActionType.Webhook && (
            <div className="row">
              <div className="col">
                {/* Webhook URL */}
                <InputText
                  title={t('webhook_url')}
                  value={formData.webhook?.url || ''}
                  onChange={(value) => handleInputChange('webhook', { ...formData.webhook, url: value })}
                  error={errors.webhook_url}
                  placeholder="https://example.com/webhook"
                />

                {/* Toggle Switches */}
                <div className="webhook-switches row row-cols-1 row-cols-md-2 g-3 mb-3">
                  <div className="col">
                    <InputSwitch
                      id="webhook-use-params"
                      title={t('use_parameters_for_webhook_body')}
                      value={formData.webhook?.use_params || false}
                      onChange={(value) => {
                        handleInputChange('webhook', { ...formData.webhook, use_params: value })
                      }}
                      horizontal={true}
                    />
                  </div>
                  <div className="col">
                    <InputSwitch
                      id="webhook-as-json"
                      title={t('send_webhook_payload_as_json')}
                      value={formData.webhook?.as_json || false}
                      onChange={(value) => {
                        handleInputChange('webhook', { ...formData.webhook, as_json: value })
                      }}
                      horizontal={true}
                    />
                  </div>
                </div>

                {/* Conditional Fields */}
                {formData.webhook?.use_params ? (
                  <InputEntries
                    title={t('webhook_params')}
                    value={formData.webhook?.params || {}}
                    onChange={(value) => handleInputChange('webhook', { ...formData.webhook, params: value })}
                    error={errors.webhook_params}
                  />
                ) : (
                  <div className="mb-3">
                    <label className="form-label d-flex align-items-center gap-2">
                      <span>{t('webhook_body')}</span>
                      <Dropdown show={showWebhookBodyPlaceholderDropdown} onToggle={setShowWebhookBodyPlaceholderDropdown}>
                        <Dropdown.Toggle
                          ref={webhookBodyPlaceholderButtonRef}
                          variant="link"
                          size="sm"
                          className="p-0 text-muted"
                          style={{ textDecoration: 'none', lineHeight: '1' }}
                          title={t('available_placeholders') || 'Dostupni placeholder-i'}
                        >
                          <i className="bi bi-code-square" style={{ fontSize: '1.1em' }}></i>
                        </Dropdown.Toggle>
                        <Dropdown.Menu className="shadow" style={{ maxHeight: '400px', overflowY: 'auto', minWidth: '300px' }}>
                          <Dropdown.Header>
                            <small className="fw-bold">
                              {t('placeholders_available_for_all_trigger_types') || 'Placeholder-i Dostupni za Sve Tipove Okidača'}
                            </small>
                          </Dropdown.Header>
                          {placeholdersAllTriggers.map((item, index) => (
                            <Dropdown.Item
                              key={`all-${index}`}
                              onClick={() => handleWebhookBodyPlaceholderSelect(item.placeholder)}
                              className="d-flex flex-column align-items-start"
                              style={{ padding: '0.5rem 1rem' }}
                            >
                              <code className="text-primary mb-1">{item.placeholder}</code>
                              <small className="text-muted">{item.description}</small>
                            </Dropdown.Item>
                          ))}
                          <Dropdown.Divider />
                          <Dropdown.Header>
                            <small className="fw-bold">
                              {t('placeholders_available_only_for_added_or_updated_triggers') ||
                                "Placeholder-i Dostupni Samo za Okidače 'Dodat' ili 'Ažuriran'"}
                            </small>
                          </Dropdown.Header>
                          {placeholdersAddedUpdated.map((item, index) => (
                            <Dropdown.Item
                              key={`added-${index}`}
                              onClick={() => handleWebhookBodyPlaceholderSelect(item.placeholder)}
                              className="d-flex flex-column align-items-start"
                              style={{ padding: '0.5rem 1rem' }}
                            >
                              <code className="text-primary mb-1">{item.placeholder}</code>
                              <small className="text-muted">{item.description}</small>
                            </Dropdown.Item>
                          ))}
                          {placeholdersCustomFields.length > 0 && (
                            <>
                              <Dropdown.Divider />
                              <Dropdown.Header>
                                <small className="fw-bold">{t('placeholders_custom_fields_section_title')}</small>
                              </Dropdown.Header>
                              {placeholdersCustomFields.map((item, index) => (
                                <Dropdown.Item
                                  key={`cf-${index}`}
                                  onClick={() => handleWebhookBodyPlaceholderSelect(item.placeholder)}
                                  className="d-flex flex-column align-items-start"
                                  style={{ padding: '0.5rem 1rem' }}
                                >
                                  <code className="text-primary mb-1">{item.placeholder}</code>
                                  <small className="text-muted">{item.description}</small>
                                </Dropdown.Item>
                              ))}
                            </>
                          )}
                        </Dropdown.Menu>
                      </Dropdown>
                    </label>
                    <Form.Control
                      ref={webhookBodyInputRef}
                      as="textarea"
                      rows={4}
                      value={formData.webhook?.body || ''}
                      onChange={(e) => handleInputChange('webhook', { ...formData.webhook, body: e.target.value })}
                      placeholder="A new {{document_type}} has been added to the system."
                      isInvalid={!!errors.webhook_body}
                    />
                    {errors.webhook_body && <Form.Control.Feedback type="invalid">{errors.webhook_body}</Form.Control.Feedback>}
                    {t('webhook_body_hint') && <small className="form-text text-muted">{t('webhook_body_hint')}</small>}
                  </div>
                )}

                {/* Webhook Headers */}
                <InputEntries
                  title={t('webhook_headers')}
                  value={formData.webhook?.headers || {}}
                  onChange={(value) => handleInputChange('webhook', { ...formData.webhook, headers: value })}
                  error={errors.webhook_headers}
                />

                {/* Include Document */}
                <InputSwitch
                  id="webhook-include-document"
                  title={t('include_document')}
                  value={formData.webhook?.include_document || false}
                  onChange={(value) => handleInputChange('webhook', { ...formData.webhook, include_document: value })}
                  horizontal={true}
                />
              </div>
            </div>
          )}
        </Form>
      </Modal.Body>

      <Modal.Footer>
        <div className="d-flex justify-content-between w-100">
          <div>
            {isEdit && onDelete && (
              <IfPermissions action={PermissionAction.Delete} type={PermissionType.Workflow}>
                <Button variant="outline-danger" onClick={handleDelete} disabled={loading}>
                  <i className="bi bi-trash me-1"></i>
                  {t('delete')}
                </Button>
              </IfPermissions>
            )}
          </div>
          <div>
            <Button variant="outline-secondary" onClick={onClose} disabled={loading} className="me-2">
              {t('cancel')}
            </Button>
            <Button variant="primary" onClick={handleSave} disabled={loading}>
              {loading && <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>}
              {t('save')}
            </Button>
          </div>
        </div>
      </Modal.Footer>

      <DocumentationDialog
        show={showDocsDialog}
        onHide={() => setShowDocsDialog(false)}
        docKey="workflow_placeholders"
      />
    </Modal>
  )
}
